import * as React from 'react';
import {Helmet} from 'react-helmet';
import {useRoute} from 'react-router5';
import PageContainer, {PageContainerProps} from 'containers/PageContainer';
import CourseBuilder from 'components/CourseBuilder';
import {WebinarBuilder} from 'components/WebinarBuilder';
import * as pages from '../pages';
import {RouteName} from "./routes";

/**
 * Render the page of route
 * @constructor
 * @return {React.ReactElement}
 */
export default function Router(): React.ReactElement {
  const {route} = useRoute();

  /**
   * Checks if the landing page should to be rendered
   * @return {Boolean}
   */
  function checkLandingShouldRender(): Boolean {
    const routeName: RouteName = route?.name;

    const mainPageRouteNames: RouteName[] = [
      'landing',
      'landing.about',
      'landing.faq',
      'landing.blog',
      'landing.feedback'
    ];

    return mainPageRouteNames.includes(routeName);
  }

  /**
   * Get the page container
   * @return {React.ComponentType<PageContainerProps> | 'div'}
   */
  function getPageContainer(): React.ComponentType<PageContainerProps> | 'div' {
    const withoutPageContainer: RouteName[] = [
      'login',
      'register',
      'landing',
      'landing.about',
      'landing.faq',
      'landing.blog',
      'landing.feedback',
      'logout',
    ];

    if (route?.name && withoutPageContainer.includes(route.name)) {
      return 'div';
    }

    return PageContainer;
  }

  /**
   * Get the page component
   * @return {JSX.Element}
   */
  function getPageComponent() {
    if (checkLandingShouldRender()) {
      return pages.LandingPage;
    }

    switch (route?.name) {
      case 'login': return pages.SignIn;
      case 'register': return pages.SignUp;
      case 'courses': return pages.Courses;
      case 'courses.create': return CourseBuilder;
      case 'courses.course': return pages.CourseProgram;
      case 'courses.course.edit': return pages.EditCourse;
      case 'courses.course.program': return pages.CourseProgram;
      case 'dashboard': return pages.Dashboard;
      case 'lessons': return pages.Lessons;
      case 'lessons.create': return pages.CreateLesson;
      case 'lessons.lesson': return pages.ViewLesson;
      case 'lessons.lesson.edit': return pages.EditLesson;
      case 'webinars': return pages.Webinars;
      case 'webinars.create': return WebinarBuilder;
      case 'webinars.webinar.edit': return pages.EditWebinar;
      case 'webinars.webinar.view': return pages.ViewWebinar;
      case 'library': return pages.Library;
      case 'settings': return pages.Settings;
      case 'teachers.teacher.courses.course': return pages.CoursePromo;
      case 'payments': return pages.Payments;
      case 'students': return pages.Students;
      case 'students.student': return pages.ViewStudent;
      case 'tariffs': return pages.Tariffs;
      case 'tasks': return pages.Tasks;
      case 'tasks.create': return pages.CreateTask;
      case 'tasks.task.view': return pages.ViewTask;
      case 'tasks.task.progress': return pages.TaskProgress;
      case 'tasks.task.edit': return pages.EditTask;
      case 'logout': return pages.Logout;
      case 'recipes': return pages.Recipes;
      case 'recipes.create': return pages.CreateRecipe;
      case 'recipes.recipe.view': return pages.RecipeView;
      case 'recipes.recipe.edit': return pages.EditRecipe;
      case 'ingredients': return pages.Ingredients;
      case 'ingredients.create': return pages.CreateIngredient;
      case 'exercises': return pages.Exercises;
      case 'exercises.create': return pages.CreateExercise;
      case 'exercises.exercise.edit': return pages.EditExercise;
      case 'trainings': return pages.Trainings;
      case 'trainings.create': return pages.CreateTraining;
      case 'trainings.training.view': return pages.TrainingView;
      case 'trainings.training.edit': return pages.EditTraining;
      case 'foods': return pages.Foods;
      case 'foods.create': return pages.CreateFood;
      case 'foods.food.view': return pages.FoodView;
      case 'foods.food.edit': return pages.EditFood;
      default: return pages.NotFound;
    }
  }

  const Container = getPageContainer();
  const Component = getPageComponent();

  return (
    <React.Fragment>
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>
      <Container>
        <Component />
      </Container>
    </React.Fragment>
  );
}
